import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useBrand, useProductName } from "@/context/BrandContext";
import api, { formatApiError, formatApiRecovery } from "@/lib/api";
import { agentToolbarVisibility, canUseManualDialActions } from "@/lib/agentToolbar";
import { formatInboundGroupNames, normalizeInboundGroups, toggleInboundGroupId } from "@/lib/agentInboundGroups";
import AgentVoicemailInbox from "@/components/AgentVoicemailInbox";
import AgentWebphone from "@/components/AgentWebphone";
import DateTimeLocalPicker from "@/components/DateTimeLocalPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowsOut, CaretDown, CalendarBlank, ChatCircle, Microphone, MicrophoneSlash, Pause, Phone, PhoneDisconnect, PhoneIncoming,
  Play, Record, SignOut, User, Key,
} from "@phosphor-icons/react";
import { sanitizeHtml } from "@/components/RichHtmlEditorDialog";

const SESSION_COPY = {
  connecting: "Starting audio session…",
  ringing: "Your phone is ringing — answer to join the conference",
  in_conference: "Connected to conference",
  online: "Direct Phone session established",
  ready: "Session established",
  on_call: "On call — customer connected",
  failed: "Session failed",
  ended: "Session ended",
};

const SESSION_COPY_WEB = {
  connecting: "Registering Webphone…",
  ringing: "Webphone is ringing — press the green Answer button below",
  answering: "Webphone answered — joining conference…",
  in_conference: "Connected to conference",
  online: "Direct Phone session established",
  ready: "Session established",
  on_call: "On call — customer connected",
  failed: "Session failed",
  ended: "Session ended",
};

function formatHms(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

function formatDuration(sec) {
  if (sec == null || Number.isNaN(sec)) return "0:00";
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

function formatStatsDuration(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  return `${m}:${String(r).padStart(2, "0")}`;
}

function normalizeCallbacks(data) {
  if (!data) {
    return { today_count: 0, due_count: 0, items: [], due_now: [], reminders: [] };
  }
  const items = data.items || [];
  const today = data.today_count ?? data.due_count ?? 0;
  return {
    today_count: today,
    due_count: today,
    items,
    due_now: data.due_now || items.filter((i) => i.due_now),
    reminders: data.reminders || items.filter((i) => i.reminder && !i.due_now),
  };
}

const CB_DISMISSED_KEY = "wa_agent_cb_dismissed";

function loadDismissedCallbackIds() {
  try {
    const raw = sessionStorage.getItem(CB_DISMISSED_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter(Boolean) : []);
  } catch {
    return new Set();
  }
}

function saveDismissedCallbackIds(set) {
  try {
    sessionStorage.setItem(CB_DISMISSED_KEY, JSON.stringify([...set].slice(-200)));
  } catch {
    /* ignore */
  }
}

function Field({ label, children, className = "", readOnly }) {
  return (
    <label className={`flex flex-col gap-0.5 min-w-0 ${className}`}>
      <span className="text-[11px] text-muted-foreground leading-tight">{label}</span>
      <div className={readOnly ? "opacity-80" : ""}>{children}</div>
    </label>
  );
}

function customerIdentityKey(customer, session) {
  const pending = session?.pending_disposition;
  return (
    (customer && (customer.lead_id || customer.phone))
    || session?.current_call?.call_id
    || pending?.call_id
    || pending?.lead_id
    || ""
  );
}

function formFromCustomer(customer) {
  const f = (customer && customer.form) || {};
  return {
    title: f.title || "",
    first_name: f.first_name || "",
    middle_name: f.middle_name || "",
    last_name: f.last_name || "",
    address1: f.address1 || "",
    address2: f.address2 || "",
    address3: f.address3 || "",
    city: f.city || "",
    state: f.state || "",
    postal_code: f.postal_code || "",
    province: f.province || "",
    vendor_id: f.vendor_id || "",
    gender: f.gender || "U",
    mobile: f.mobile || "",
    dial_code: f.dial_code || customer?.phone_code || "",
    phone: f.phone || customer?.phone || "",
    alt_phone: f.alt_phone || "",
    email: f.email || customer?.email || "",
    comments: f.comments || customer?.notes || "",
  };
}

function ActionBtn({
  label, icon: Icon, onClick, disabled, active, danger, testid, title,
}) {
  return (
    <button
      type="button"
      title={title || label}
      disabled={disabled}
      onClick={(e) => {
        // Fire immediately — do not wait for React state / busy paint
        if (disabled) return;
        onClick?.(e);
      }}
      data-testid={testid}
      className={
        "flex flex-col items-center justify-center gap-1 w-full py-3 px-1 rounded-md border text-[11px] font-medium transition-colors active:scale-[0.97] "
        + (disabled
          ? "border-transparent text-muted-foreground/45 cursor-not-allowed bg-transparent"
          : danger
            ? "border-destructive/30 text-destructive hover:bg-destructive/10"
            : active
              ? "border-primary/30 bg-primary/10 text-primary"
              : "border-border bg-card hover:bg-muted text-foreground")
      }
    >
      <Icon
        className={`w-6 h-6 ${disabled ? "" : danger ? "text-destructive" : active ? "text-primary" : ""}`}
        weight={active || danger ? "fill" : "regular"}
      />
      <span className="leading-tight text-center px-0.5">{label}</span>
    </button>
  );
}

export default function AgentWorkspace() {
  const { user, logout } = useAuth();
  const { brand } = useBrand();
  const productName = useProductName();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [phase, setPhase] = useState("boot");
  const [tick, setTick] = useState(0);
  const [dispoCode, setDispoCode] = useState("");
  const [dispoNotes, setDispoNotes] = useState("");
  const [dispoPauseAgent, setDispoPauseAgent] = useState(false);
  const [dispoCallbackAt, setDispoCallbackAt] = useState("");
  const [dispoCallbackPrivate, setDispoCallbackPrivate] = useState(false);
  const [callbacksOpen, setCallbacksOpen] = useState(false);
  const [callbacks, setCallbacks] = useState({
    today_count: 0, due_count: 0, items: [], due_now: [], reminders: [],
  });
  const [rescheduleId, setRescheduleId] = useState("");
  const [rescheduleAt, setRescheduleAt] = useState("");
  const [dueModalCb, setDueModalCb] = useState(null);
  const [callbackReminder, setCallbackReminder] = useState(null);
  const dismissedDueRef = useRef(loadDismissedCallbackIds());
  const dueHydratedRef = useRef(false);
  const knownDueIdsRef = useRef(new Set());
  const [dayStats, setDayStats] = useState(null);
  const [statsOpen, setStatsOpen] = useState(false);
  const [recoveryHint, setRecoveryHint] = useState("");
  const [notice, setNotice] = useState("");
  const [mainTab, setMainTab] = useState("customer"); // customer | script | disposition
  const [form, setForm] = useState(formFromCustomer(null));
  const [statusSince, setStatusSince] = useState(() => Date.now());
  const [queueLive, setQueueLive] = useState(null);
  const [manualCallOpen, setManualCallOpen] = useState(false);
  const [manualCallNumber, setManualCallNumber] = useState("");
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferType, setTransferType] = useState("agent");
  const [transferTargetId, setTransferTargetId] = useState("");
  const [transferPhone, setTransferPhone] = useState("");
  const [transferTargets, setTransferTargets] = useState({
    agents: [], inbound_groups: [], campaigns: [],
    third_party: { available: false, vendor_name: null },
  });
  const [transferTargetsLoading, setTransferTargetsLoading] = useState(false);
  const [ingroupOpen, setIngroupOpen] = useState(false);
  const [ingroupDraft, setIngroupDraft] = useState([]);
  const [ingroupBusy, setIngroupBusy] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [messageChannel, setMessageChannel] = useState("sms"); // sms | email
  const [messageProviderId, setMessageProviderId] = useState("");
  const [messageTo, setMessageTo] = useState("");
  const [messageSubject, setMessageSubject] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const pollRef = useRef(null);
  const lastCallIdRef = useRef(null);
  const lastEventSeqRef = useRef(0);
  const lastStatusRef = useRef("");
  const submittingRef = useRef(false);
  const formKeyRef = useRef("");
  const formDirtyRef = useRef(false);
  const saveFormTimerRef = useRef(null);
  const refreshSeqRef = useRef(0);
  const disposedCallsRef = useRef(new Set());
  const [webphoneEnabled, setWebphoneEnabled] = useState(null); // null=unknown
  const [webphoneCallState, setWebphoneCallState] = useState(""); // ringing|answering|incall|…
  // Mobile: Session info (General / Call Details / Agents) collapses so Customer
  // Information + Script keep a usable viewport (flex min-h-0 was shrinking them to 0).
  const [mobileInfoOpen, setMobileInfoOpen] = useState(false);
  const webphoneEnabledRef = useRef(null); // keep startSession stable (avoid busy stale-closure)
  const webRegRef = useRef(false);
  const webReadyWaitersRef = useRef([]);
  const webphoneJoinBurstRef = useRef(null);

  const mode = session?.agent_session || campaign?.agent_session || workspace?.campaign?.agent_session || "conference";
  const isDirect = mode === "direct_phone";

  const onWebphoneRegistered = useCallback(() => {
    webRegRef.current = true;
    webphoneEnabledRef.current = true;
    setWebphoneEnabled(true);
    const waiters = webReadyWaitersRef.current.splice(0);
    waiters.forEach((fn) => { try { fn(true); } catch { /* */ } });
  }, []);

  useEffect(() => {
    webphoneEnabledRef.current = webphoneEnabled;
  }, [webphoneEnabled]);

  const waitForWebphoneRegister = useCallback(async (timeoutMs = 25000) => {
    if (webRegRef.current) return true;
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        webReadyWaitersRef.current = webReadyWaitersRef.current.filter((fn) => fn !== settle);
        resolve(webRegRef.current);
      }, timeoutMs);
      const settle = (ok) => {
        clearTimeout(timer);
        resolve(!!ok || webRegRef.current);
      };
      webReadyWaitersRef.current.push(settle);
      if (webRegRef.current) settle(true);
    });
  }, []);

  // Non-overlapping poll: workspace refresh hits FreeSWITCH (~1–5s). A fixed
  // setInterval(2s) stacked requests; refreshSeqRef then discarded every result
  // so the Conference Session gate stayed on "ringing" after Answer.
  const pollMsRef = useRef(3000);
  const pollActiveRef = useRef(false);
  const pollTimerRef = useRef(null);
  const refreshInFlightRef = useRef(false);

  const clearPoll = () => {
    pollActiveRef.current = false;
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    // Keep pollRef truthy-compatible with older checks (interval id / flag)
    pollRef.current = null;
  };

  const ensurePoll = useCallback((ms) => {
    if (typeof ms === "number" && ms > 0) pollMsRef.current = ms;
    if (pollActiveRef.current) return;
    pollActiveRef.current = true;
    pollRef.current = 1;
    const tick = async () => {
      if (!pollActiveRef.current) return;
      try {
        await refreshRef.current?.();
      } catch { /* network blip — next tick retries */ }
      if (!pollActiveRef.current) return;
      pollTimerRef.current = setTimeout(tick, pollMsRef.current);
    };
    tick();
  }, []);

  const refreshRef = useRef(null);

  // Only gate on submittingRef — never close over React `busy` state.
  // startSession is memoized; if it captured busy=true (e.g. after setWebphoneEnabled
  // during an in-flight init), Retry would silently no-op forever while the button
  // still looked clickable.
  const runLocked = async (action, fn) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setBusyAction(action);
    setError("");
    try {
      await fn();
    } finally {
      submittingRef.current = false;
      setBusy(false);
      setBusyAction("");
    }
  };

  const signOut = async () => {
    if (session?.on_call) {
      const ok = window.confirm(
        "You are on an active customer call. Logging out will disconnect the customer and end your session. Continue?",
      );
      if (!ok) return;
    }
    clearPoll();
    try {
      const { data } = await api.post("/agent/session/logout");
      if (data?.message) toast.message(data.message);
    } catch {
      /* best-effort */
    }
    sessionStorage.removeItem("wa_agent_campaign");
    sessionStorage.removeItem("wa_agent_session");
    await logout();
    navigate("/agent/login", { replace: true });
  };

  const applySession = useCallback((s) => {
    setSession(s);
    if (!s) return;
    const st = `${s.agent_status || ""}:${s.status || ""}:${s.on_call ? 1 : 0}`;
    if (st !== lastStatusRef.current) {
      lastStatusRef.current = st;
      setStatusSince(Date.now());
    }
    const direct = s.agent_session === "direct_phone";
    if (s.status === "failed") {
      setPhase(direct ? "workspace" : "connecting");
      setError(s.error || "Audio session failed");
      setRecoveryHint(s.error_recovery || "");
      clearPoll();
      return;
    }
    if (
      s.workspace_ready
      || s.status === "in_conference"
      || s.status === "ready"
      || s.status === "online"
      || s.status === "on_call"
    ) {
      setPhase("workspace");
      setError(s.error || "");
      setRecoveryHint(s.error_recovery || "");
      sessionStorage.setItem("wa_agent_session", JSON.stringify({ id: s.id, status: s.status }));
    } else if (s.status === "connecting" || s.status === "ringing") {
      setPhase("connecting");
      setError("");
      setRecoveryHint("");
    }

    const cid = s.current_call?.call_id;
    const seq = s.current_call?.event_seq || 0;
    const callState = (s.current_call?.state || "").toLowerCase();
    if (cid && cid !== lastCallIdRef.current && s.on_call) {
      lastCallIdRef.current = cid;
      lastEventSeqRef.current = seq;
      if (callState === "dialing") toast.message("Dialing…");
      else if (callState === "ringing" || callState === "connecting") toast.message("Ringing…");
      else if (callState === "answered" || callState === "in_progress") toast.message("Call connected");
      else toast.message("Call in progress");
      setMainTab("customer");
    } else if (!s.on_call) {
      lastCallIdRef.current = null;
    } else if (seq > lastEventSeqRef.current) {
      lastEventSeqRef.current = seq;
      if (callState === "ringing") toast.message("Ringing…");
      else if (callState === "answered" || callState === "in_progress") toast.message("Call connected");
    }
  }, []);

  const applyWorkspace = useCallback((data) => {
    if (!data) return;
    let next = data;
    // Light action responses omit slow side-panels — keep last full poll values.
    if (data.light) {
      setWorkspace((prev) => {
        const merged = {
          ...(prev || {}),
          ...data,
          agents_board: (prev && prev.agents_board) || data.agents_board,
          callbacks: (prev && prev.callbacks) || data.callbacks,
          day_stats: prev && Object.prototype.hasOwnProperty.call(prev, "day_stats")
            ? prev.day_stats
            : data.day_stats,
          messaging: (prev && prev.messaging) || data.messaging,
          dialing: (prev && prev.dialing && data.dialing?.pd_available !== false)
            ? { ...prev.dialing, ...data.dialing, pd_available: prev.dialing.pd_available }
            : data.dialing,
        };
        return merged;
      });
      if (data.session) applySession(data.session);
      if (data.campaign) {
        setCampaign((prev) => ({ ...(prev || {}), ...data.campaign }));
      }
      if (data.queue) setQueueLive(data.queue);
      const sess = data.session;
      const holding = Boolean(
        sess?.on_call || sess?.disposition_required || sess?.pending_disposition,
      );
      const key = customerIdentityKey(data.customer, sess);
      if (data.customer && key && key !== formKeyRef.current) {
        formKeyRef.current = key;
        formDirtyRef.current = false;
        setForm(formFromCustomer(data.customer));
      } else if (!holding && !data.customer) {
        formKeyRef.current = "";
        formDirtyRef.current = false;
        setForm(formFromCustomer(null));
      }
      return;
    }
    const pendingId = data.session?.pending_disposition?.call_id;
    const liveId = data.session?.current_call?.call_id;
    if (
      (pendingId && disposedCallsRef.current.has(pendingId))
      || (liveId && disposedCallsRef.current.has(liveId) && !data.session?.on_call)
    ) {
      next = {
        ...data,
        customer: null,
        session: data.session
          ? {
            ...data.session,
            disposition_required: false,
            pending_disposition: null,
            on_call: false,
            current_call: data.session.current_call?.call_id
              && disposedCallsRef.current.has(data.session.current_call.call_id)
              ? null
              : data.session.current_call,
          }
          : data.session,
      };
    }
    setWorkspace(next);
    if (next.session) applySession(next.session);
    if (next.callbacks) setCallbacks(normalizeCallbacks(next.callbacks));
    if (Object.prototype.hasOwnProperty.call(next, "day_stats")) {
      setDayStats(next.day_stats || null);
    }
    if (next.campaign) {
      setCampaign((prev) => ({ ...(prev || {}), ...next.campaign }));
      try {
        sessionStorage.setItem("wa_agent_campaign", JSON.stringify(next.campaign));
      } catch {
        /* ignore */
      }
    }
    if (next.queue) setQueueLive((prev) => prev ?? next.queue);
    const sess = next.session;
    const holding = Boolean(
      sess?.on_call || sess?.disposition_required || sess?.pending_disposition,
    );
    const key = customerIdentityKey(next.customer, sess);
    if (next.customer && key && key !== formKeyRef.current) {
      formKeyRef.current = key;
      formDirtyRef.current = false;
      setForm(formFromCustomer(next.customer));
    } else if (!holding && !next.customer) {
      formKeyRef.current = "";
      formDirtyRef.current = false;
      setForm(formFromCustomer(null));
    }
    const dial = next.dialing || {};
    const firstNotice = (next.notices && next.notices[0]) || null;
    if (dial.error_code && dial.message) setNotice(dial.message);
    else if (dial.idle_hint) setNotice(dial.idle_hint);
    else if (firstNotice?.message) setNotice(firstNotice.message);
    else if (next.last_call_error) setNotice(next.last_call_error);
    else setNotice("");
  }, [applySession]);

  const refresh = useCallback(async () => {
    // Single-flight: overlapping /agent/workspace calls (SSH/ESL) used to discard
    // every result via refreshSeqRef while Answer was still Establishing.
    if (refreshInFlightRef.current) return null;
    refreshInFlightRef.current = true;
    const seq = ++refreshSeqRef.current;
    try {
      const { data } = await api.get("/agent/workspace");
      if (seq === refreshSeqRef.current) applyWorkspace(data);
      return data?.session || null;
    } catch (e) {
      const status = e.response?.status;
      if (status === 401 || status === 403) {
        await logout();
        navigate("/agent/login", { replace: true });
        return null;
      }
      try {
        const { data } = await api.get("/agent/session");
        if (seq === refreshSeqRef.current) applySession(data.session);
        return data.session;
      } catch (e2) {
        if (e2.response?.status === 401 || e2.response?.status === 403) {
          await logout();
          navigate("/agent/login", { replace: true });
          return null;
        }
        setError(formatApiError(e.response?.data?.detail || e2.response?.data?.detail, e));
        return null;
      }
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [applySession, applyWorkspace, logout, navigate]);

  refreshRef.current = refresh;

  const startSession = useCallback(async (retry = false) => {
    await runLocked(retry ? "retry" : "init", async () => {
      setPhase("connecting");
      try {
        // Web Phone: REGISTER in the browser before FreeSWITCH originates user/ext
        let wpOn = webphoneEnabledRef.current;
        if (wpOn == null) {
          try {
            const { data: wp } = await api.get("/agent/webphone");
            wpOn = !!wp?.enabled;
            webphoneEnabledRef.current = wpOn;
            setWebphoneEnabled(wpOn);
          } catch {
            wpOn = false;
            webphoneEnabledRef.current = false;
            setWebphoneEnabled(false);
          }
        }
        if (wpOn) {
          // Let React paint the Webphone dock before we block on REGISTER
          await new Promise((r) => setTimeout(r, 100));
          const ok = await waitForWebphoneRegister(25000);
          if (!ok) {
            setError("Webphone did not register. Allow microphone access and check your connection.");
            setRecoveryHint("Keep this page open — the Webphone must show Registered before the conference can ring.");
            return;
          }
        }
        const { data } = await api.post(retry ? "/agent/session/retry" : "/agent/session/init");
        applySession(data.session);
        if (data.message) toast.message(data.message);
        clearPoll();
        const direct = data.session?.agent_session === "direct_phone";
        if (!direct && (data.session?.status === "connecting" || data.session?.status === "ringing")) {
          ensurePoll(2000);
        } else {
          await refresh();
        }
      } catch (e) {
        const detail = e.response?.data?.detail;
        setError(formatApiError(detail, e));
        setRecoveryHint(formatApiRecovery(detail) || "");
        setPhase("connecting");
      }
    });
  }, [applySession, ensurePoll, refresh, waitForWebphoneRegister]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("wa_agent_campaign");
      if (raw) setCampaign(JSON.parse(raw));
    } catch {
      /* ignore */
    }
    (async () => {
      const s = await refresh();
      if (s && (s.status === "connecting" || s.status === "ringing")) {
        setPhase("connecting");
        clearPoll();
        ensurePoll(2000);
      } else if (s && (s.workspace_ready || ["in_conference", "ready", "online", "on_call"].includes(s.status))) {
        setPhase("workspace");
        clearPoll();
        if (s.is_ready || s.on_call || s.disposition_required || s.status === "ready" || s.status === "on_call") {
          ensurePoll(s.on_call ? 2000 : 3000);
        }
      } else if (!s || s.status === "failed" || s.status === "ended") {
        await startSession(false);
      }
    })();
    return () => clearPoll();
  }, [ensurePoll, refresh, startSession]);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const dismissDueCallback = useCallback((id, { dismissAllDue = false } = {}) => {
    if (!id && !dismissAllDue) return;
    if (id) dismissedDueRef.current.add(id);
    if (dismissAllDue) {
      (callbacks.due_now || []).forEach((d) => {
        if (d?.id) dismissedDueRef.current.add(d.id);
      });
    }
    saveDismissedCallbackIds(dismissedDueRef.current);
    setDueModalCb(null);
  }, [callbacks.due_now]);

  // Soft 15‑min reminder (banner) + due modal only for NEWLY due callbacks (not already-missed spam)
  useEffect(() => {
    if (phase !== "workspace") return;
    const reminders = callbacks.reminders || [];
    const undismissedReminder = reminders.find((r) => r?.id && !dismissedDueRef.current.has(r.id));
    if (undismissedReminder) {
      setCallbackReminder({
        id: undismissedReminder.id,
        text: `Callback in ~15 min: ${undismissedReminder.customer_name || undismissedReminder.phone || "customer"} · ${undismissedReminder.scheduled_display || ""}`.trim(),
      });
    } else {
      setCallbackReminder(null);
    }

    const dueList = callbacks.due_now || [];
    const dueIds = new Set(dueList.map((d) => d.id).filter(Boolean));

    // First workspace snapshot: seed known ids; never auto-open already-missed pile
    if (!dueHydratedRef.current) {
      dueHydratedRef.current = true;
      knownDueIdsRef.current = dueIds;
      dueList.forEach((d) => {
        if (d?.id && d.missed) dismissedDueRef.current.add(d.id);
      });
      saveDismissedCallbackIds(dismissedDueRef.current);
      return;
    }

    // Only pop modal for callbacks that just became due (new id vs previous poll)
    const newlyDue = dueList.find(
      (d) => d?.id
        && !knownDueIdsRef.current.has(d.id)
        && !dismissedDueRef.current.has(d.id),
    );
    knownDueIdsRef.current = dueIds;

    if (callbacksOpen || dueModalCb) {
      // Don't stack / re-open while panel or another due modal is up
      return;
    }
    if (newlyDue) {
      setDueModalCb(newlyDue);
    }
  }, [phase, callbacks.reminders, callbacks.due_now, callbacksOpen, dueModalCb]);

  useEffect(() => {
    if (phase !== "workspace") return;
    if (webphoneJoinBurstRef.current) {
      clearInterval(webphoneJoinBurstRef.current);
      webphoneJoinBurstRef.current = null;
    }
    if (session?.on_call || session?.is_ready || session?.disposition_required) {
      clearPoll();
      const st = String(session?.current_call?.state || "").toLowerCase();
      const dialing = ["dialing", "ringing", "connecting", "assigned"].includes(st);
      ensurePoll(session?.on_call ? (dialing ? 1500 : 2000) : 3000);
      return;
    }
    // Paused / Not Ready — keep a slow poll so status stays authoritative
    if (session?.agent_status === "not_ready" || session?.agent_status === "wrapup") {
      clearPoll();
      ensurePoll(5000);
    }
  }, [ensurePoll, phase, session?.disposition_required, session?.is_ready, session?.on_call, session?.current_call?.state, session?.agent_status]);

  useEffect(() => () => {
    if (webphoneJoinBurstRef.current) {
      clearInterval(webphoneJoinBurstRef.current);
      webphoneJoinBurstRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (phase !== "workspace") return undefined;
    let cancelled = false;
    const pull = async () => {
      try {
        const { data } = await api.get("/agent/queue");
        if (cancelled || !data) return;
        if (typeof data.calls_in_queue === "number") {
          setQueueLive({
            calls_in_queue: data.calls_in_queue,
            emails_in_queue: data.emails_in_queue ?? 0,
          });
        }
        if (data.agents_board) {
          setWorkspace((prev) => (prev ? { ...prev, agents_board: data.agents_board } : prev));
        }
      } catch {
        /* workspace poll remains the fallback */
      }
    };
    pull();
    const t = setInterval(pull, 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [phase]);

  useEffect(() => {
    if (session?.disposition_required) setMainTab("disposition");
    else setMainTab((tab) => (tab === "disposition" ? "customer" : tab));
  }, [session?.disposition_required]);

  const setAgentStatus = (status) => {
    if (submittingRef.current) return;
    // Optimistic UI — flip banner/buttons immediately; server remains authoritative.
    const prevSnapshot = session;
    setSession((prev) => (prev ? {
      ...prev,
      agent_status: status,
      agent_status_label: status === "ready" ? "Ready" : status === "wrapup" ? "Wrap-up" : "Not Ready",
      status: status === "ready" ? "ready" : (prev.status === "ready" ? "in_conference" : prev.status),
      is_ready: status === "ready",
    } : prev));
    setStatusSince(Date.now());
    runLocked(status, async () => {
      try {
        const { data } = await api.post("/agent/session/status", { status });
        if (data.workspace) applyWorkspace(data.workspace);
        else applySession(data.session);
        if (data.message) toast.success(data.message);
        clearPoll();
        // Ready: poll for inbound assigns. Paused/wrap-up: slow poll so a stale
        // refresh cannot silently flip the banner back without a user action.
        ensurePoll(status === "ready" ? 3000 : 5000);
        // Background full snapshot (board/callbacks) — do not block the rail.
        refresh();
      } catch (e) {
        if (prevSnapshot) applySession(prevSnapshot);
        const detail = e.response?.data?.detail;
        setError(formatApiError(detail, e));
        setRecoveryHint(formatApiRecovery(detail) || "");
        refresh();
      }
    });
  };

  const hangupCall = () => runLocked("hangup", async () => {
    try {
      const { data } = await api.post("/agent/session/hangup-call");
      applySession(data.session);
      setDispoCode("");
      setDispoNotes("");
      toast.message(data.message || "Call ended — submit disposition, then click Ready.");
      clearPoll();
      setMainTab("disposition");
      refresh();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
    }
  });

  const toggleMute = () => {
    if (submittingRef.current) return;
    const nextMuted = !session?.muted;
    setSession((prev) => {
      if (!prev) return prev;
      const call = { ...(prev.current_call || {}), muted: nextMuted };
      return { ...prev, muted: nextMuted, current_call: call };
    });
    runLocked("mute", async () => {
      try {
        const { data } = await api.post("/agent/session/mute", {});
        if (data?.session) applySession(data.session);
        toast.message(data?.message || (data?.muted ? "Muted" : "Unmuted"));
      } catch (e) {
        setSession((prev) => {
          if (!prev) return prev;
          const call = { ...(prev.current_call || {}), muted: !nextMuted };
          return { ...prev, muted: !nextMuted, current_call: call };
        });
        setError(formatApiError(e.response?.data?.detail, e));
      }
    });
  };

  const toggleHold = () => {
    if (submittingRef.current) return;
    const nextHold = !session?.on_hold;
    setSession((prev) => {
      if (!prev) return prev;
      const call = { ...(prev.current_call || {}), on_hold: nextHold };
      return { ...prev, on_hold: nextHold, current_call: call };
    });
    runLocked("hold", async () => {
      try {
        const { data } = await api.post("/agent/session/hold", {});
        if (data?.session) applySession(data.session);
        toast.message(data?.message || (data?.on_hold ? "Customer on hold" : "Customer resumed"));
      } catch (e) {
        setSession((prev) => {
          if (!prev) return prev;
          const call = { ...(prev.current_call || {}), on_hold: !nextHold };
          return { ...prev, on_hold: !nextHold, current_call: call };
        });
        setError(formatApiError(e.response?.data?.detail, e));
      }
    });
  };

  const startRecording = () => {
    if (submittingRef.current) return;
    setSession((prev) => (prev ? { ...prev, recording: true } : prev));
    runLocked("record", async () => {
      try {
        const { data } = await api.post("/agent/session/record", { recording: true });
        if (data?.session) applySession(data.session);
      } catch (e) {
        setSession((prev) => (prev ? { ...prev, recording: false } : prev));
        setError(formatApiError(e.response?.data?.detail, e));
      }
    });
  };

  const loadTransferTargets = async () => {
    setTransferTargetsLoading(true);
    try {
      const { data } = await api.get("/agent/session/transfer-targets");
      setTransferTargets({
        agents: data.agents || [],
        inbound_groups: data.inbound_groups || [],
        campaigns: data.campaigns || [],
        third_party: data.third_party || { available: false, vendor_name: null },
      });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
    } finally {
      setTransferTargetsLoading(false);
    }
  };

  const transferPhoneDigits = (transferPhone || "").replace(/\D/g, "");
  const transferReady = transferType === "third_party"
    ? transferPhoneDigits.length >= 7 && transferPhoneDigits.length <= 18
    : Boolean(transferTargetId);

  const submitTransfer = (stay) => runLocked("transfer", async () => {
    if (transferType === "third_party") {
      if (transferPhoneDigits.length < 7 || transferPhoneDigits.length > 18) {
        setError("Enter a valid 3rd party number (7–18 digits).");
        return;
      }
    } else if (!transferTargetId) {
      setError("Select a transfer destination.");
      return;
    }
    try {
      const payload = {
        target_type: transferType,
        stay: !!stay,
      };
      if (transferType === "third_party") {
        payload.phone_number = (transferPhone || "").trim();
      } else {
        payload.target_id = transferTargetId;
      }
      const { data } = await api.post("/agent/session/transfer", payload);
      setTransferOpen(false);
      setTransferTargetId("");
      setTransferPhone("");
      toast.success(data?.message || "Transfer started");
      if (data?.session) applySession(data.session);
      if (data?.workspace) applyWorkspace(data.workspace);
      else refresh();
      if (!stay) setMainTab("disposition");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
    }
  });

  const submitManualCall = () => {
    const raw = (manualCallNumber || "").trim();
    const digits = raw.replace(/[^\d+]/g, "");
    if (digits.replace(/\D/g, "").length < 7) {
      setError("Enter a valid phone number (at least 7 digits).");
      return;
    }
    // Close modal immediately — don't wait for originate to finish
    setManualCallOpen(false);
    setManualCallNumber("");
    const optimisticPhone = digits.replace(/\D/g, "").length ? digits : raw;
    setSession((prev) => (prev ? {
      ...prev,
      on_call: true,
      agent_status: "on_call",
      agent_status_label: "On Call",
      status: "on_call",
      current_call: {
        call_id: prev.current_call?.call_id || `pending:${Date.now()}`,
        state: "dialing",
        started_at: new Date().toISOString(),
        customer: { phone: optimisticPhone },
      },
    } : prev));
    setStatusSince(Date.now());
    clearPoll();
    ensurePoll(1500);
    runLocked("manual_dial", async () => {
      try {
        const { data } = await api.post("/agent/session/manual-dial", { phone_number: raw });
        if (data?.ok === false) {
          setError(data.message || data.error_code || "Manual Call failed");
          await refresh();
          return;
        }
        toast.message(data?.message || "Dialing…");
        if (data?.session) applySession(data.session);
        if (data?.workspace) applyWorkspace(data.workspace);
        else await refresh();
        clearPoll();
        ensurePoll(1500);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail, e));
        await refresh();
      }
    });
  };

  const refreshCallbacks = async () => {
    try {
      const { data } = await api.get("/agent/session/callbacks");
      setCallbacks(normalizeCallbacks(data));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
    }
  };

  const callbackCallNow = (cb) => runLocked("callback_call", async () => {
    try {
      const { data } = await api.post(`/agent/session/callbacks/${cb.id}/call-now`);
      if (data.callbacks) setCallbacks(normalizeCallbacks(data.callbacks));
      const phone = (data.phone || cb.phone || "").trim();
      if (!phone) {
        setError("Callback has no phone number.");
        return;
      }
      dismissDueCallback(cb.id, { dismissAllDue: false });
      setCallbackReminder((prev) => (prev?.id === cb.id ? null : prev));
      setCallbacksOpen(false);
      const optimisticPhone = phone.replace(/\D/g, "") || phone;
      setSession((prev) => (prev ? {
        ...prev,
        on_call: true,
        agent_status: "on_call",
        agent_status_label: "On Call",
        status: "on_call",
        current_call: {
          call_id: prev.current_call?.call_id || `pending:${Date.now()}`,
          state: "dialing",
          started_at: new Date().toISOString(),
          customer: { phone: optimisticPhone },
        },
      } : prev));
      setStatusSince(Date.now());
      clearPoll();
      ensurePoll(1500);
      const dial = await api.post("/agent/session/manual-dial", { phone_number: phone });
      if (dial.data?.ok === false) {
        setError(dial.data.message || dial.data.error_code || "Call Now failed");
        await refresh();
        return;
      }
      toast.message(dial.data?.message || "Dialing callback…");
      if (dial.data?.session) applySession(dial.data.session);
      if (dial.data?.workspace) applyWorkspace(dial.data.workspace);
      else await refresh();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
      await refresh();
    }
  });

  const callbackReschedule = (cbId) => runLocked("callback_reschedule", async () => {
    if (!rescheduleAt) {
      setError("Select a new callback date and time.");
      return;
    }
    try {
      const { data } = await api.patch(`/agent/session/callbacks/${cbId}`, {
        callback_at: rescheduleAt,
      });
      if (data.callbacks) setCallbacks(normalizeCallbacks(data.callbacks));
      setRescheduleId("");
      setRescheduleAt("");
      toast.success("Callback rescheduled");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
    }
  });

  const callbackDelete = (cbId) => runLocked("callback_delete", async () => {
    try {
      const { data } = await api.delete(`/agent/session/callbacks/${cbId}`);
      if (data.callbacks) setCallbacks(normalizeCallbacks(data.callbacks));
      if (rescheduleId === cbId) {
        setRescheduleId("");
        setRescheduleAt("");
      }
      toast.success("Callback deleted");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
    }
  });

  const submitDisposition = () => runLocked("disposition", async () => {
    const pending = session?.pending_disposition || workspace?.pending_disposition;
    if (!pending?.call_id) {
      setError("No call awaiting disposition.");
      return;
    }
    if (!dispoCode) {
      setError("Select a disposition code.");
      return;
    }
    if (dispoCode === "CALLBACK" && !dispoCallbackAt) {
      setError("Select a callback date and time.");
      return;
    }
    try {
      refreshSeqRef.current += 1;
      disposedCallsRef.current.add(pending.call_id);
      const payload = {
        call_id: pending.call_id,
        code: dispoCode,
        notes: dispoNotes.trim() || null,
        pause_agent: Boolean(dispoPauseAgent),
        customer_form: form,
      };
      if (dispoCode === "CALLBACK") {
        payload.callback_at = dispoCallbackAt;
        payload.callback_private = Boolean(dispoCallbackPrivate);
      }
      const { data } = await api.post("/agent/session/disposition", payload);
      if (data.workspace) applyWorkspace(data.workspace);
      else applySession(data.session);
      if (data.workspace?.callbacks) setCallbacks(normalizeCallbacks(data.workspace.callbacks));
      formKeyRef.current = "";
      formDirtyRef.current = false;
      setForm(formFromCustomer(null));
      setMainTab("customer");
      setDispoCode("");
      setDispoNotes("");
      setDispoPauseAgent(false);
      setDispoCallbackAt("");
      setDispoCallbackPrivate(false);
      toast.success(data.message || "Disposition saved");
      clearPoll();
      ensurePoll(3000);
      setMainTab("customer");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
      refresh();
    }
  });

  const logo = brand.logo_url || brand.login_logo_url;
  const webphoneOn = webphoneEnabled === true;
  const statusCopy = webphoneOn ? SESSION_COPY_WEB : SESSION_COPY;
  const webphoneJoining = webphoneCallState === "answering" || webphoneCallState === "incall";
  const statusLabel = (
    webphoneJoining
    && (session?.status === "ringing" || session?.status === "connecting")
  )
    ? statusCopy.answering
    : (statusCopy[session?.status] || session?.status || "…");
  const showConferenceGate = !isDirect && (phase === "connecting" || session?.status === "failed");

  const webphoneDock = (
    <AgentWebphone
      gateMode={showConferenceGate}
      defaultExpanded={showConferenceGate}
      onRegistered={onWebphoneRegistered}
      onStatusChange={(st) => {
        setWebphoneCallState(st || "");
        if (st === "registered" || st === "answering" || st === "incall" || st === "ringing") {
          setWebphoneEnabled(true);
        }
        // Answer clicked / media establishing — burst-refresh so the gate
        // leaves "ringing" without waiting for the slow accept()+ICE finish.
        if (st === "answering" || st === "incall") {
          try { refreshRef.current?.(); } catch { /* */ }
          if (webphoneJoinBurstRef.current) {
            clearInterval(webphoneJoinBurstRef.current);
            webphoneJoinBurstRef.current = null;
          }
          let n = 0;
          webphoneJoinBurstRef.current = setInterval(() => {
            n += 1;
            try { refreshRef.current?.(); } catch { /* */ }
            if (n >= 12) {
              clearInterval(webphoneJoinBurstRef.current);
              webphoneJoinBurstRef.current = null;
            }
          }, 700);
        }
      }}
    />
  );
  const agentInfo = workspace?.agent;
  const campInfo = workspace?.campaign || campaign;
  const inboundGroups = normalizeInboundGroups(
    workspace?.inbound_groups || campaign?.inbound_groups,
  );
  const availableIngroups = inboundGroups.available || [];
  const selectedIngroups = inboundGroups.selected || [];
  const selectedIngroupIds = inboundGroups.selected_ids
    || selectedIngroups.map((g) => g.id).filter(Boolean);
  const sipInfo = workspace?.sip;
  const agentStatus = session?.agent_status || workspace?.status?.current || "not_ready";
  const agentStatusLabel = session?.agent_status_label
    || workspace?.status?.label
    || (agentStatus === "ready" ? "Ready" : agentStatus === "wrapup" ? "Wrap-up" : agentStatus === "on_call" ? "On Call" : "Not Ready");

  const onCall = Boolean(session?.on_call);
  const activeCall = onCall ? (session?.current_call || workspace?.call) : null;
  const callState = String(activeCall?.state || "").toLowerCase();
  const isDialingPhase = ["dialing", "connecting", "ringing", "assigned"].includes(callState);
  const isLiveTalking = onCall && !isDialingPhase && (
    callState === "answered" || callState === "in_progress" || !callState
  );
  const pendingDispo = session?.pending_disposition || workspace?.pending_disposition || null;
  const dispoRequired = Boolean(session?.disposition_required || workspace?.disposition?.required);
  const visibleTab = mainTab === "disposition" && !dispoRequired ? "customer" : mainTab;
  const dispoOptions = workspace?.disposition?.options || [];
  const customerInfo = workspace?.customer || null;
  const queue = queueLive || workspace?.queue || { calls_in_queue: 0, emails_in_queue: 0 };
  const queueWaiting = Number(queue.calls_in_queue || 0) > 0;
  const prevQueueRef = useRef(0);
  useEffect(() => {
    const n = Number(queue.calls_in_queue || 0);
    if (n > prevQueueRef.current) {
      toast.message(n === 1 ? "Customer waiting in queue" : `${n} customers waiting in queue`);
    }
    prevQueueRef.current = n;
  }, [queue.calls_in_queue]);

  // Prefer live workspace campaign over sessionStorage snapshot (stale after campaign edit)
  const dialingType = workspace?.campaign?.dialing_type || campaign?.dialing_type || campInfo?.dialing_type || "";
  const campaignType = workspace?.campaign?.campaign_type || campaign?.campaign_type || campInfo?.campaign_type || "outbound";
  const toolbar = useMemo(
    () => agentToolbarVisibility(dialingType, campaignType),
    [dialingType, campaignType],
  );
  const dialActionsOk = canUseManualDialActions({
    dialingType,
    campaignType,
    agentStatus,
    onCall,
    dispositionRequired: dispoRequired,
    busy,
  });
  const messaging = workspace?.messaging || { enabled: false, sms: [], email: [] };
  const messagingSms = messaging.sms || [];
  const messagingEmail = messaging.email || [];
  const messagingEnabled = Boolean(
    messaging.enabled || messagingSms.length || messagingEmail.length,
  );
  const messageProviders = messageChannel === "email" ? messagingEmail : messagingSms;

  const openMessageModal = () => {
    const preferSms = messagingSms.length > 0;
    const channel = preferSms ? "sms" : "email";
    const providers = preferSms ? messagingSms : messagingEmail;
    setMessageChannel(channel);
    setMessageProviderId(providers[0]?.id || "");
    setMessageTo(
      channel === "sms"
        ? (form.phone || customerInfo?.phone || "")
        : (form.email || customerInfo?.email || ""),
    );
    setMessageSubject("");
    setMessageBody("");
    setMessageOpen(true);
  };

  const submitOutboundMessage = () => runLocked("send_message", async () => {
    const to = (messageTo || "").trim();
    const bodyText = (messageBody || "").trim();
    if (!messageProviderId) {
      setError("Select a provider");
      return;
    }
    if (!to) {
      setError(messageChannel === "sms" ? "Enter a phone number" : "Enter an email address");
      return;
    }
    if (!bodyText) {
      setError("Enter a message");
      return;
    }
    try {
      const { data } = await api.post("/agent/messages/send", {
        channel: messageChannel,
        provider_id: messageProviderId,
        to,
        subject: messageChannel === "email" ? (messageSubject || "").trim() : undefined,
        body: bodyText,
      });
      toast.success(data?.message || (messageChannel === "sms" ? "SMS sent" : "Email sent"));
      setMessageOpen(false);
      setMessageBody("");
      setMessageSubject("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
    }
  });

  let displayDuration = activeCall?.duration_sec;
  if (onCall && displayDuration == null && activeCall?.started_at) {
    try {
      const start = new Date(activeCall.answered_at || activeCall.started_at).getTime();
      displayDuration = Math.max(0, Math.floor((Date.now() - start) / 1000));
    } catch {
      displayDuration = 0;
    }
  }

  const callStatusLabel = callState === "dialing"
    ? "DIALING"
    : (callState === "ringing" || callState === "connecting")
      ? "RINGING"
      : isLiveTalking
        ? "INCALL"
        : null;

  const bannerStatus = callStatusLabel
    || (agentStatus === "ready"
      ? "READY"
      : agentStatus === "wrapup"
        ? "WRAPUP"
        : "PAUSED");
  const bannerElapsed = onCall && displayDuration != null
    ? formatHms(displayDuration)
    : formatHms(Math.floor((Date.now() - statusSince) / 1000));
  void tick;

  // Show status strip whenever dialing/ringing/on-call — even Manual Outbound
  const showStatusBanner = toolbar.showStatusBanner || onCall || isDialingPhase;

  const bannerTone = callStatusLabel === "DIALING"
    ? "bg-amber-400 text-slate-900"
    : callStatusLabel === "RINGING"
      ? "bg-orange-400 text-slate-900"
      : isLiveTalking
        ? "bg-[#f87171] text-white"
        : agentStatus === "ready"
          ? "bg-[#7dd3fc] text-slate-900"
          : "bg-[#facc15] text-slate-900";

  const statusBlockTone = isLiveTalking
    ? "bg-[#ef4444] text-white"
    : isDialingPhase
      ? "bg-amber-500 text-white"
      : agentStatus === "ready"
        ? "bg-[#38bdf8] text-slate-900"
        : "bg-[#eab308] text-slate-900";

  const sipLabel = session?.sip_username || sipInfo?.username || agentInfo?.extension || "—";
  const inputCls = "h-8 rounded-sm border-border bg-background text-sm";
  const roCls = `${inputCls} bg-muted/60`;

  const persistCustomerForm = useCallback((next) => {
    if (saveFormTimerRef.current) clearTimeout(saveFormTimerRef.current);
    saveFormTimerRef.current = setTimeout(async () => {
      try {
        await api.post("/agent/session/customer-info", { form: next });
      } catch {
        /* keep local edits; workspace poll must not wipe them */
      }
    }, 700);
  }, []);

  useEffect(() => () => {
    if (saveFormTimerRef.current) clearTimeout(saveFormTimerRef.current);
  }, []);

  const setFormField = (key) => (e) => {
    const v = e?.target ? e.target.value : e;
    formDirtyRef.current = true;
    setForm((prev) => {
      const next = { ...prev, [key]: v };
      persistCustomerForm(next);
      return next;
    });
  };

  // Right-side dock — stays mounted across gate ↔ workspace so SIP REGISTER survives.
  const webphonePortal = typeof document !== "undefined"
    ? createPortal(
      <div
        className={`fixed bottom-0 right-0 w-64 max-w-[100vw] border-l border-t border-border bg-white shadow-lg ${
          showConferenceGate ? "z-[70]" : "z-40"
        }`}
        data-testid="agent-webphone-dock"
      >
        {webphoneDock}
      </div>,
      document.body,
    )
    : null;

  return (
    <>
    {showConferenceGate && (
      <div className="fixed inset-0 z-50 bg-background flex flex-col" data-testid="agent-workspace-page">
        <header className="border-b border-border px-4 h-12 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            {logo ? <img src={logo} alt="" className="w-7 h-7 object-contain" /> : (
              <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground grid place-items-center">
                <User className="w-4 h-4" weight="fill" />
              </div>
            )}
            <span className="text-sm font-semibold">{productName || "WACalls"}</span>
          </div>
          <div className="flex items-center gap-1">
            <AgentVoicemailInbox variant="header" />
            <Button variant="ghost" size="sm" onClick={signOut} data-testid="agent-ws-logout">
              <SignOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </header>
        <main className="flex-1 flex items-start sm:items-center justify-center p-6 pb-8 overflow-y-auto">
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-6" data-testid="agent-session-connect">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 text-primary grid place-items-center">
                <PhoneIncoming className="w-5 h-5" weight="fill" />
              </div>
              <div>
                <h1 className="text-xl font-semibold tracking-tight">Conference Session</h1>
                <p className="text-sm text-muted-foreground">
                  {campInfo?.name ? `Campaign: ${campInfo.name}` : "Connecting audio"}
                </p>
              </div>
            </div>
            <p className="text-sm" data-testid="agent-session-status-text">{statusLabel}</p>
            {session?.sip_username && (
              <p className="text-xs mt-3 text-muted-foreground" data-testid="agent-session-sip">
                {webphoneOn || webphoneEnabled !== false ? (
                  <>
                    Answer on the Webphone below as{" "}
                    <span className="font-mono font-medium text-foreground">{session.sip_username}</span>
                  </>
                ) : (
                  <>
                    Softphone must be logged in as{" "}
                    <span className="font-mono font-medium text-foreground">{session.sip_username}</span>
                  </>
                )}
              </p>
            )}
            {error && <p className="text-sm text-destructive mt-4" data-testid="agent-session-error">{error}</p>}
            {recoveryHint && <p className="text-xs text-muted-foreground mt-2" data-testid="agent-session-recovery">{recoveryHint}</p>}
            <div className="mt-6 flex gap-2">
              {(session?.status === "failed" || error) && (
                <Button className="flex-1 h-11" disabled={busy} onClick={() => startSession(true)} data-testid="agent-session-retry">
                  {busy ? "Retrying…" : "Retry"}
                </Button>
              )}
              <Button variant="outline" onClick={signOut}>Cancel</Button>
            </div>
            {(session?.status === "ringing" || session?.status === "connecting") && !webphoneOn && (
              <div className="mt-6 flex justify-center">
                <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
              </div>
            )}
            <p className="mt-5 text-xs text-muted-foreground" data-testid="agent-session-webphone-hint">
              {webphoneOn || webphoneEnabled !== false
                ? "Keep the Webphone Registered (right). Press Answer when it rings — then click Retry if needed."
                : null}
            </p>
          </div>
        </main>
      </div>
    )}
    <div
      className={`h-[100dvh] bg-[#f3f4f6] text-foreground flex flex-col overflow-hidden ${
        showConferenceGate ? "hidden" : ""
      }`}
      data-testid={showConferenceGate ? undefined : "agent-workspace-page"}
      aria-hidden={showConferenceGate || undefined}
    >
      {/* Top chrome */}
      <header className="h-11 shrink-0 bg-white border-b border-border px-3 flex items-center justify-between" data-testid="agent-ws-topbar">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 grid place-items-center overflow-hidden">
            {logo ? <img src={logo} alt="" className="w-full h-full object-cover" /> : <User className="w-4 h-4" weight="fill" />}
          </div>
          <div className="min-w-0 hidden sm:block">
            <p className="text-xs font-semibold truncate" data-testid="agent-info-name">
              {agentInfo?.name || user?.name || user?.email}
            </p>
            <p className="text-[10px] text-muted-foreground font-mono truncate" data-testid="agent-info-extension">{sipLabel}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <span
            className={`text-[11px] font-bold tracking-wide px-2 py-1 rounded ${
              isLiveTalking
                ? "bg-red-100 text-red-700"
                : isDialingPhase
                  ? "bg-amber-100 text-amber-800"
                  : "bg-slate-100 text-slate-600"
            }`}
            data-testid="agent-ws-live-call"
            title={activeCall?.customer?.phone ? `To ${activeCall.customer.phone}` : undefined}
          >
            {callStatusLabel || (onCall ? "LIVE CALL" : "NO LIVE CALL")}
            {isDialingPhase && activeCall?.customer?.phone ? (
              <span className="font-mono font-normal ml-1.5 opacity-80">
                {activeCall.customer.phone}
              </span>
            ) : null}
          </span>
          <button
            type="button"
            className={`relative p-1.5 rounded hover:bg-muted ${
              callbacksOpen ? "bg-muted text-foreground" : "text-muted-foreground"
            }`}
            title="Call backs"
            onClick={() => {
              if (callbacksOpen) {
                setCallbacksOpen(false);
                setRescheduleId("");
                setRescheduleAt("");
              } else {
                setCallbacksOpen(true);
                refreshCallbacks();
              }
            }}
            data-testid="agent-ws-callbacks"
          >
            <CalendarBlank className="w-4 h-4" weight={callbacks.today_count ? "fill" : "regular"} />
            {callbacks.today_count > 0 ? (
              <span
                className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-0.5 rounded-full bg-red-600 text-white text-[9px] font-bold leading-3.5 grid place-items-center"
                data-testid="agent-ws-callbacks-due"
                title="Today's callbacks"
              >
                {callbacks.today_count > 9 ? "9+" : callbacks.today_count}
              </span>
            ) : null}
          </button>
          <AgentVoicemailInbox variant="header" />
          <button
            type="button"
            className="p-1.5 rounded hover:bg-muted text-muted-foreground"
            title="Toggle fullscreen"
            onClick={() => {
              if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
              else document.exitFullscreen?.();
            }}
            data-testid="agent-ws-fullscreen"
          >
            <ArrowsOut className="w-4 h-4" />
          </button>
          <button
            type="button"
            className="p-1.5 rounded hover:bg-muted text-muted-foreground"
            title="Logout"
            onClick={signOut}
            data-testid="agent-ws-logout"
          >
            <SignOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {callbackReminder ? (
        <div
          className="shrink-0 px-3 py-1.5 bg-amber-50 border-b border-amber-200 text-amber-900 text-xs flex items-center justify-between gap-2"
          data-testid="agent-callback-reminder"
        >
          <span className="truncate">{callbackReminder.text}</span>
          <button
            type="button"
            className="shrink-0 underline font-medium"
            onClick={() => {
              setCallbacksOpen(true);
              refreshCallbacks();
            }}
          >
            View
          </button>
        </div>
      ) : null}

      {/* Status banner — always on during dialing/ringing/incall; else campaign toolbar rules */}
      {(showStatusBanner || queueWaiting) && (
        <div
          className={`h-8 shrink-0 px-3 flex items-center justify-between text-xs font-semibold ${
            queueWaiting && !isLiveTalking ? "agent-queue-banner-alert" : bannerTone
          }`}
          data-testid="agent-ws-status-banner"
          data-queue-waiting={queueWaiting ? "true" : "false"}
        >
          <span data-testid="agent-ws-status-label">
            {bannerStatus}{" "}
            <span className="font-mono tabular-nums ml-1" data-testid="agent-ws-status-timer">{bannerElapsed}</span>
            {isDialingPhase && activeCall?.customer?.phone ? (
              <span className="font-mono font-normal ml-2 opacity-90" data-testid="agent-ws-dial-number">
                → {activeCall.customer.phone}
              </span>
            ) : null}
          </span>
          <span
            className="inline-flex items-center gap-1.5 font-medium"
            data-testid="agent-ws-queue-stats"
          >
            {queueWaiting ? <span className="agent-queue-dot" aria-hidden /> : null}
            Calls in Queue:{" "}
            <span
              className={queueWaiting ? "agent-queue-count-alert" : ""}
              data-testid="agent-ws-queue-count"
            >
              {queue.calls_in_queue ?? 0}
            </span>
            , Emails in Queue: {queue.emails_in_queue ?? 0}
          </span>
        </div>
      )}

      {(error || notice) && (
        <div className="shrink-0 px-3 py-1.5 bg-white border-b border-border text-xs" data-testid="agent-ws-alerts">
          {error && <p className="text-destructive" data-testid="agent-ws-error">{error}</p>}
          {recoveryHint && <p className="text-muted-foreground" data-testid="agent-ws-recovery">{recoveryHint}</p>}
          {!error && notice && <p className="text-muted-foreground" data-testid="agent-ws-notice">{notice}</p>}
        </div>
      )}

      {/* Body: left actions | center | right info.
          Mobile is a vertical stack — allow the page to scroll and never let the
          Session-info column shrink Customer Information / Script to height 0. */}
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-y-auto lg:overflow-hidden">
        {/* Left action rail — scrolls only when buttons overflow the viewport */}
        <aside
          className="shrink-0 min-h-0 bg-white border-b lg:border-b-0 lg:border-r border-border w-full lg:w-[7.5rem] lg:max-h-full lg:overflow-y-auto p-2 grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-1 gap-1.5 content-start"
          data-testid="agent-status-controls"
        >
          {toolbar.showReady && !onCall && agentStatus !== "ready" && (
            <ActionBtn
              label="Ready"
              icon={Play}
              disabled={busy || session?.can_ready === false}
              onClick={() => setAgentStatus("ready")}
              testid="agent-ready-btn"
              title={session?.can_ready === false ? "Complete session setup before Ready" : "Go Ready — take inbound / queued calls"}
            />
          )}
          {toolbar.showPause && !onCall && agentStatus === "ready" && (
            <ActionBtn
              label="Pause"
              icon={Phone}
              disabled={busy}
              onClick={() => setAgentStatus("not_ready")}
              testid="agent-not-ready-btn"
              title="Not Ready — pause dialing"
            />
          )}
          {toolbar.showDialNext && (
            <ActionBtn
              label="Dial Next"
              icon={Phone}
              disabled={!dialActionsOk}
              title={
                onCall
                  ? "Hang up before Dial Next"
                  : "Dial the next number from the assigned list"
              }
              onClick={() => {
                if (submittingRef.current) return;
                // Instant feedback — originate continues in background lock
                setSession((prev) => (prev ? {
                  ...prev,
                  on_call: true,
                  agent_status: "on_call",
                  agent_status_label: "On Call",
                  status: "on_call",
                  current_call: {
                    call_id: prev.current_call?.call_id || `pending-dn:${Date.now()}`,
                    state: "dialing",
                    started_at: new Date().toISOString(),
                    customer: prev.current_call?.customer || {},
                  },
                } : prev));
                setStatusSince(Date.now());
                clearPoll();
                ensurePoll(1500);
                toast.message("Dialing…");
                runLocked("dial_next", async () => {
                  try {
                    const { data } = await api.post("/agent/session/dial-next", {});
                    if (data?.ok === false) {
                      setError(data.message || data.error_code || "Dial Next failed");
                      refresh();
                      return;
                    }
                    if (data?.message) toast.message(data.message);
                    if (data?.session) applySession(data.session);
                    if (data?.workspace) applyWorkspace(data.workspace);
                    else refresh();
                    clearPoll();
                    ensurePoll(1500);
                  } catch (e) {
                    setError(formatApiError(e.response?.data?.detail, e));
                    refresh();
                  }
                });
              }}
              testid="agent-manual-btn"
            />
          )}
          {toolbar.showHangup && (
            <ActionBtn
              label="Hangup Call"
              icon={PhoneDisconnect}
              danger
              disabled={busy || !onCall || session?.can_hangup === false}
              onClick={hangupCall}
              testid="agent-hangup-btn"
              title={onCall ? "Hang up the customer call" : "No live call"}
            />
          )}
          {toolbar.showTransfer && (
            <ActionBtn
              label="Transfer Call"
              icon={Key}
              disabled={busy || !onCall || session?.can_transfer === false}
              title={onCall ? "Transfer this call" : "No live call to transfer"}
              onClick={() => {
                setTransferType("agent");
                setTransferTargetId("");
                setTransferPhone("");
                setTransferOpen(true);
                loadTransferTargets();
              }}
              testid="agent-transfer-btn"
            />
          )}
          {toolbar.showManualCall && (
            <ActionBtn
              label="Manual Call"
              icon={PhoneIncoming}
              disabled={!dialActionsOk}
              title={
                onCall
                  ? "Hang up before Manual Call"
                  : "Enter a number to dial"
              }
              onClick={() => {
                setManualCallNumber("");
                setManualCallOpen(true);
              }}
              testid="agent-manual-call-btn"
            />
          )}
          {toolbar.showMute && (
            <ActionBtn
              label={session?.muted ? "Unmute" : "Mute"}
              icon={session?.muted ? MicrophoneSlash : Microphone}
              active={!!session?.muted}
              danger={!!session?.muted}
              disabled={busy || !onCall || session?.can_mute === false}
              title={onCall ? (session?.muted ? "Unmute your mic" : "Mute your mic") : "No live call"}
              onClick={toggleMute}
              testid="agent-mute-btn"
            />
          )}
          {toolbar.showHold && (
            <ActionBtn
              label={session?.on_hold ? "Resume" : "Hold"}
              icon={session?.on_hold ? Play : Pause}
              active={!!session?.on_hold}
              disabled={busy || !onCall || session?.can_hold === false}
              title={onCall ? (session?.on_hold ? "Take the customer off hold" : "Put the customer on hold") : "No live call"}
              onClick={toggleHold}
              testid="agent-hold-btn"
            />
          )}
          {toolbar.showRecord && (session?.call_recording === "on_demand" || workspace?.campaign?.call_recording === "on_demand") && (
            <ActionBtn
              label={session?.recording ? "Recording" : "Record"}
              icon={Record}
              active={!!session?.recording}
              disabled={busy || !onCall || !!session?.recording || session?.can_record === false}
              title={
                session?.recording
                  ? "Call is being recorded"
                  : onCall
                    ? "Start recording this call"
                    : "No live call"
              }
              onClick={startRecording}
              testid="agent-record-btn"
            />
          )}
          {messagingEnabled && (
            <ActionBtn
              label="Messages"
              icon={ChatCircle}
              disabled={false}
              onClick={openMessageModal}
              testid="agent-messages-btn"
              title="Send SMS or Email"
            />
          )}
        </aside>

        {/* Center: tabs + form — on mobile size to content (flex-none) so fields stay visible */}
        <main
          className="flex-none lg:flex-1 min-w-0 min-h-[55dvh] lg:min-h-0 flex flex-col bg-white border-r border-border"
          data-testid="agent-workspace"
        >
          <div className="shrink-0 flex items-end gap-4 px-4 pt-2 border-b border-border overflow-x-auto">
            {[
              { id: "customer", label: "Customer Information", testid: "tab-customer" },
              { id: "script", label: "Script", testid: "tab-script" },
              ...(dispoRequired ? [{ id: "disposition", label: "Disposition", testid: "tab-disposition" }] : []),
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={t.testid}
                onClick={() => setMainTab(t.id)}
                className={
                  "pb-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap "
                  + (visibleTab === t.id
                    ? "border-sky-600 text-sky-700"
                    : "border-transparent text-muted-foreground hover:text-foreground")
                }
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex-1 lg:min-h-0 overflow-visible lg:overflow-auto p-4">
            {phase === "boot" && !session && (
              <p className="text-sm text-muted-foreground" data-testid="agent-ws-loading">Loading workspace…</p>
            )}

            {visibleTab === "customer" && (
              <div className="space-y-3 max-w-4xl" data-testid="customer-info-panel">
                {!onCall && !dispoRequired && !customerInfo && (
                  <p className="text-sm text-muted-foreground" data-testid="customer-info-empty">
                    Waiting for a customer call. Customer fields populate when a lead is assigned.
                  </p>
                )}
                {customerInfo?.is_new_caller && (
                  <p
                    className="text-sm text-sky-800 bg-sky-50 border border-sky-200 rounded-sm px-3 py-2"
                    data-testid="customer-new-caller-banner"
                  >
                    New caller — no saved Inbound history for this number yet. Enter details below.
                  </p>
                )}
                {customerInfo && !customerInfo.is_new_caller && customerInfo.list_kind === "inbound" && (
                  <p
                    className="text-xs text-muted-foreground"
                    data-testid="customer-known-inbound"
                  >
                    Known inbound caller
                    {customerInfo.list_name ? ` · ${customerInfo.list_name}` : ""}
                    {customerInfo.call_count != null ? ` · ${customerInfo.call_count} prior call(s)` : ""}
                  </p>
                )}
                <Field label="Title">
                  <Input className={inputCls} value={form.title} onChange={setFormField("title")} data-testid="customer-field-title" />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="First Name">
                    <Input className={inputCls} value={form.first_name} onChange={setFormField("first_name")} data-testid="customer-info-name" />
                  </Field>
                  <Field label="Middle Name">
                    <Input className={inputCls} value={form.middle_name} onChange={setFormField("middle_name")} />
                  </Field>
                  <Field label="Last Name">
                    <Input className={inputCls} value={form.last_name} onChange={setFormField("last_name")} />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Address1">
                    <Input className={inputCls} value={form.address1} onChange={setFormField("address1")} />
                  </Field>
                  <Field label="Address2">
                    <Input className={inputCls} value={form.address2} onChange={setFormField("address2")} />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Address3">
                    <Input className={inputCls} value={form.address3} onChange={setFormField("address3")} />
                  </Field>
                  <Field label="City">
                    <Input className={inputCls} value={form.city} onChange={setFormField("city")} />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="State">
                    <Input className={inputCls} value={form.state} onChange={setFormField("state")} />
                  </Field>
                  <Field label="PostCode">
                    <Input className={inputCls} value={form.postal_code} onChange={setFormField("postal_code")} />
                  </Field>
                  <Field label="Province">
                    <Input className={inputCls} value={form.province} onChange={setFormField("province")} />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="Vendor ID">
                    <Input className={inputCls} value={form.vendor_id} onChange={setFormField("vendor_id")} />
                  </Field>
                  <Field label="Gender">
                    <Select value={form.gender || "U"} onValueChange={(v) => setFormField("gender")(v)}>
                      <SelectTrigger className={`${inputCls} h-8`} data-testid="customer-field-gender">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="U">U - Undefined</SelectItem>
                        <SelectItem value="M">M - Male</SelectItem>
                        <SelectItem value="F">F - Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Mobile number">
                    <Input
                      className={inputCls}
                      value={form.mobile}
                      onChange={setFormField("mobile")}
                      data-testid="customer-field-mobile"
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="DialCode" readOnly>
                    <Input className={roCls} value={form.dial_code} readOnly data-testid="customer-field-dialcode" />
                  </Field>
                  <Field label="Phone" readOnly>
                    <Input
                      className={roCls}
                      value={form.phone}
                      readOnly
                      disabled
                      data-testid="customer-info-phone"
                    />
                  </Field>
                  <Field label="Alt Phone">
                    <Input className={inputCls} value={form.alt_phone} onChange={setFormField("alt_phone")} />
                  </Field>
                </div>
                <Field label="Email">
                  <Input className={inputCls} type="email" value={form.email} onChange={setFormField("email")} data-testid="customer-info-email" />
                </Field>
                <Field label="Comments">
                  <Textarea
                    className="min-h-[120px] rounded-sm text-sm"
                    value={form.comments}
                    onChange={setFormField("comments")}
                    data-testid="customer-info-notes"
                  />
                </Field>
                {Array.isArray(customerInfo?.previous_calls) && customerInfo.previous_calls.length > 0 && (
                  <div className="pt-2 border-t border-border" data-testid="customer-previous-calls">
                    <p className="text-[11px] text-muted-foreground mb-1.5">Previous calls</p>
                    <ul className="space-y-1 text-sm">
                      {customerInfo.previous_calls.slice(0, 5).map((c) => (
                        <li
                          key={c.id || `${c.started_at}-${c.disposition}`}
                          className="flex flex-wrap gap-x-3 gap-y-0.5 text-muted-foreground"
                        >
                          <span>
                            {c.started_at
                              ? new Date(c.started_at).toLocaleString()
                              : "—"}
                          </span>
                          <span>{c.disposition || c.action || "—"}</span>
                          <span>{formatDuration(c.duration_sec)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {visibleTab === "script" && (
              <div className="max-w-3xl prose prose-sm dark:prose-invert" data-testid="agent-script-panel">
                {campInfo?.script ? (
                  /<[a-z][\s\S]*>/i.test(campInfo.script) ? (
                    <div
                      className="text-sm text-foreground bg-muted/40 border border-border rounded-md p-4 agent-script-html"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(campInfo.script) }}
                    />
                  ) : (
                    <pre className="whitespace-pre-wrap font-sans text-sm text-foreground bg-muted/40 border border-border rounded-md p-4">
                      {campInfo.script}
                    </pre>
                  )
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No campaign script is configured. Ask your administrator to add one from Campaigns → Agent script.
                  </p>
                )}
              </div>
            )}

            {visibleTab === "disposition" && dispoRequired && pendingDispo && (
              <div className="max-w-lg space-y-3" data-testid="disposition-panel">
                <p className="text-sm text-muted-foreground" data-testid="agent-dispo-hint">
                  Call ended ({formatDuration(pendingDispo.duration_sec)}). Submit a disposition before going Ready.
                </p>
                <Field label="Reason code">
                  <Select value={dispoCode} onValueChange={setDispoCode}>
                    <SelectTrigger className="h-10 rounded-sm" data-testid="dispo-code-select">
                      <SelectValue placeholder="Select disposition" />
                    </SelectTrigger>
                    <SelectContent>
                      {dispoOptions.map((opt) => (
                        <SelectItem key={opt.code} value={opt.code} data-testid={`dispo-opt-${opt.code}`}>
                          {opt.label} ({opt.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Call notes">
                  <Textarea
                    className="min-h-[88px] rounded-sm"
                    value={dispoNotes}
                    onChange={(e) => setDispoNotes(e.target.value)}
                    maxLength={4000}
                    data-testid="dispo-notes"
                  />
                </Field>
                {dispoCode === "CALLBACK" && (
                  <div className="space-y-2 rounded-sm border border-border p-3 bg-slate-50" data-testid="dispo-callback-panel">
                    <Field label={`Callback date & time (${workspace?.callbacks?.timezone || workspace?.campaign?.timezone || campaign?.timezone || "UTC"})`}>
                      <DateTimeLocalPicker
                        className="h-10 rounded-sm"
                        value={dispoCallbackAt}
                        onChange={setDispoCallbackAt}
                        testid="dispo-callback-at"
                      />
                    </Field>
                    <p className="text-[11px] text-muted-foreground -mt-1">
                      Time is in your account timezone (System Settings). Same zone as the customer portal.
                    </p>
                    {Boolean(workspace?.agent?.my_call_back || agentInfo?.my_call_back) && (
                      <label
                        className="flex items-center gap-2 text-sm cursor-pointer select-none"
                        data-testid="dispo-callback-private-row"
                      >
                        <Checkbox
                          checked={dispoCallbackPrivate}
                          onCheckedChange={(v) => setDispoCallbackPrivate(v === true)}
                          data-testid="dispo-callback-private"
                        />
                        <span>My Call Back (only I see and get notified)</span>
                      </label>
                    )}
                  </div>
                )}
                <label
                  className="flex items-center gap-2 text-sm cursor-pointer select-none"
                  data-testid="dispo-pause-agent-row"
                >
                  <Checkbox
                    checked={dispoPauseAgent}
                    onCheckedChange={(v) => setDispoPauseAgent(v === true)}
                    data-testid="dispo-pause-agent"
                  />
                  <span>Pause agent</span>
                </label>
                <p className="text-xs text-muted-foreground -mt-1">
                  {dispoPauseAgent
                    ? "After save you will stay Not Ready (paused)."
                    : "After save you will go Ready for the next call."}
                </p>
                <Button
                  className="h-10 rounded-sm px-6"
                  disabled={busy || !dispoCode || (dispoCode === "CALLBACK" && !dispoCallbackAt)}
                  onClick={submitDisposition}
                  data-testid="dispo-submit-btn"
                >
                  {busyAction === "disposition" ? "Saving…" : "Save disposition"}
                </Button>
              </div>
            )}
          </div>
        </main>

        {/* Right info column — swaps to scrollable Call backs panel.
            On mobile this collapses behind a toggle so Customer Information / Script
            are not pushed off-screen by General / Call Details / Agents Status. */}
        <aside
          className={`shrink-0 w-full min-h-0 bg-white overflow-hidden border-t lg:border-t-0 flex flex-col ${
            callbacksOpen ? "lg:w-80" : "lg:w-64"
          }`}
          data-testid="agent-info-panel"
        >
          {!callbacksOpen && (
            <button
              type="button"
              className="lg:hidden w-full flex items-center justify-between gap-2 px-3 py-2.5 border-b border-border text-sm font-semibold bg-slate-50"
              onClick={() => setMobileInfoOpen((v) => !v)}
              aria-expanded={mobileInfoOpen}
              data-testid="agent-info-mobile-toggle"
            >
              <span>Session info</span>
              <CaretDown
                className={`w-4 h-4 text-muted-foreground transition-transform ${mobileInfoOpen ? "rotate-180" : ""}`}
                weight="bold"
              />
            </button>
          )}
          {callbacksOpen ? (
            <div className="flex flex-col min-h-0 flex-1" data-testid="agent-callbacks-panel">
              <div className="p-3 border-b border-border flex items-center justify-between gap-2 shrink-0">
                <p className="text-sm font-semibold">Call backs</p>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={refreshCallbacks}
                    data-testid="agent-callbacks-refresh"
                  >
                    Refresh
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => {
                      setCallbacksOpen(false);
                      setRescheduleId("");
                      setRescheduleAt("");
                    }}
                    data-testid="agent-callbacks-close"
                  >
                    Close
                  </Button>
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-2" data-testid="agent-callbacks-scroll">
                {(callbacks.items || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground py-6 text-center">No callbacks for today.</p>
                ) : (
                  (callbacks.items || []).map((cb) => (
                    <div
                      key={cb.id}
                      className={`rounded-sm border px-2.5 py-2 text-xs space-y-1.5 ${
                        cb.missed
                          ? "border-red-300 bg-red-50"
                          : cb.due
                            ? "border-amber-400 bg-amber-50"
                            : "border-border bg-slate-50"
                      }`}
                      data-testid={`agent-callback-row-${cb.id}`}
                    >
                      <div className="flex justify-between gap-2 font-semibold">
                        <span className="truncate">{cb.customer_name || cb.phone || "Callback"}</span>
                        <span className="shrink-0 flex items-center gap-1">
                          {cb.missed ? (
                            <span className="text-red-700">Missed</span>
                          ) : cb.private ? (
                            <span className="text-amber-700">My Call Back</span>
                          ) : (
                            <span className="text-muted-foreground">Campaign</span>
                          )}
                        </span>
                      </div>
                      <p className="font-mono">{cb.phone || "—"}</p>
                      <p className={cb.missed ? "text-red-800 font-medium" : cb.due ? "text-amber-800 font-medium" : "text-muted-foreground"}>
                        {cb.scheduled_display || cb.scheduled_at}
                        {cb.missed ? " · missed" : cb.due_now ? " · due now" : cb.reminder ? " · soon" : ""}
                      </p>
                      {cb.agent_name ? (
                        <p className="text-muted-foreground truncate">Scheduled by {cb.agent_name}</p>
                      ) : null}
                      {cb.campaign_name ? (
                        <p className="text-muted-foreground truncate">{cb.campaign_name}</p>
                      ) : null}
                      {(cb.comments || cb.notes) ? (
                        <p className="text-muted-foreground line-clamp-2">{cb.comments || cb.notes}</p>
                      ) : null}
                      {rescheduleId === cb.id ? (
                        <div className="space-y-1.5 pt-1 border-t border-border/60">
                          <DateTimeLocalPicker
                            className="h-8 rounded-sm text-xs"
                            value={rescheduleAt}
                            onChange={setRescheduleAt}
                            testid={`agent-callback-reschedule-at-${cb.id}`}
                          />
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              size="sm"
                              className="h-7 flex-1 text-[11px] rounded-sm"
                              disabled={busy || !rescheduleAt}
                              onClick={() => callbackReschedule(cb.id)}
                              data-testid={`agent-callback-reschedule-save-${cb.id}`}
                            >
                              Save
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-7 flex-1 text-[11px] rounded-sm"
                              onClick={() => { setRescheduleId(""); setRescheduleAt(""); }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-1 pt-1">
                          <Button
                            type="button"
                            size="sm"
                            className="h-7 px-1 text-[10px] rounded-sm"
                            disabled={busy || onCall || dispoRequired}
                            onClick={() => callbackCallNow(cb)}
                            data-testid={`agent-callback-call-now-${cb.id}`}
                          >
                            Call Now
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 px-1 text-[10px] rounded-sm"
                            disabled={busy}
                            onClick={() => {
                              setRescheduleId(cb.id);
                              setRescheduleAt("");
                            }}
                            data-testid={`agent-callback-reschedule-${cb.id}`}
                          >
                            Reschedule
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 px-1 text-[10px] rounded-sm text-destructive border-destructive/40"
                            disabled={busy}
                            onClick={() => callbackDelete(cb.id)}
                            data-testid={`agent-callback-delete-${cb.id}`}
                          >
                            Delete
                          </Button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
          <>
          <div
            className={`flex-1 min-h-0 overflow-y-auto ${mobileInfoOpen ? "block" : "hidden"} lg:block`}
            data-testid="agent-info-mobile-body"
          >
          <div className="p-3 border-b border-border">
            <p className="text-sm font-semibold mb-2">General</p>
            <dl className="space-y-1.5 text-xs">
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Username</dt>
                <dd className="font-medium truncate" data-testid="agent-info-username">{agentInfo?.username || user?.email || "—"}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Phone</dt>
                <dd className="font-mono truncate">SIP/{sipLabel}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Campaign</dt>
                <dd className="font-medium truncate text-right" data-testid="campaign-info-name">{campInfo?.name || session?.campaign_name || "—"}</dd>
              </div>
              <div className="flex justify-between gap-2 items-start">
                <dt className="text-muted-foreground pt-0.5">InGroup</dt>
                <dd className="text-right min-w-0">
                  {availableIngroups.length ? (
                    <button
                      type="button"
                      className="text-sky-700 font-medium truncate max-w-[9.5rem] hover:underline text-right"
                      title={formatInboundGroupNames(selectedIngroups)}
                      onClick={() => {
                        setIngroupDraft([...selectedIngroupIds]);
                        setIngroupOpen(true);
                      }}
                      data-testid="agent-ingroup-open"
                    >
                      {formatInboundGroupNames(selectedIngroups)}
                    </button>
                  ) : (
                    <span className="text-sky-700 font-medium truncate" data-testid="agent-ingroup-empty">—</span>
                  )}
                </dd>
              </div>
              {(workspace?.agent?.display_stats || agentInfo?.display_stats || dayStats) ? (
                <div className="flex justify-between gap-2 items-center">
                  <dt className="text-muted-foreground">Stats</dt>
                  <dd>
                    <button
                      type="button"
                      className="text-sky-700 font-medium hover:underline"
                      onClick={async () => {
                        setStatsOpen(true);
                        try {
                          const { data } = await api.get("/agent/session/day-stats");
                          if (data?.day_stats) setDayStats(data.day_stats);
                        } catch {
                          /* keep last */
                        }
                      }}
                      data-testid="agent-stats-open"
                    >
                      View
                    </button>
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>

          <div className="p-3 border-b border-border" data-testid="active-call-panel">
            <p className="text-sm font-semibold mb-2">Call Details</p>
            <dl className="space-y-1.5 text-xs">
              <div className={`flex justify-between gap-2 ${queueWaiting ? "text-orange-700 font-semibold" : ""}`}>
                <dt className={queueWaiting ? "text-orange-700" : "text-muted-foreground"}>Calls in Queue</dt>
                <dd data-testid="agent-ws-calls-in-queue" className={queueWaiting ? "agent-queue-count-alert" : ""}>
                  {queue.calls_in_queue ?? 0}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Call Time</dt>
                <dd className="font-mono" data-testid="active-call-timer">
                  {onCall ? formatDuration(displayDuration) : ""}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Channel</dt>
                <dd className="truncate max-w-[8rem]" data-testid="active-call-state">
                  {onCall ? (activeCall?.state || "in_progress") : ""}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Session ID</dt>
                <dd className="font-mono text-[10px] truncate max-w-[9rem]" title={session?.id || ""}>
                  {session?.id ? session.id.slice(0, 8) : "—"}
                </dd>
              </div>
            </dl>
          </div>

          <div className="p-3" data-testid="agent-ws-status">
            <p className="text-sm font-semibold mb-2">Agents Status</p>
            {(() => {
              const board = workspace?.agents_board || { agents: [], counts: {} };
              const counts = board.counts || {};
              const agents = board.agents || [];
              const tone = (status) => (
                status === "ready" ? "text-sky-600"
                  : status === "incall" ? "text-red-600"
                    : status === "dialing" ? "text-amber-600"
                      : "text-amber-600"
              );
              return (
                <>
                  <div className="space-y-1 text-[11px] font-semibold" data-testid="agent-ws-status-counts">
                    <p className="flex justify-between gap-2">
                      <span className="text-sky-600">READY</span>
                      <span data-testid="agent-board-ready">{counts.ready ?? 0}</span>
                    </p>
                    <p className="flex justify-between gap-2">
                      <span><span className="text-amber-600">DIALING</span> / <span className="text-orange-600">RINGING</span></span>
                      <span data-testid="agent-board-dialing">{counts.dialing ?? 0}</span>
                    </p>
                    <p className="flex justify-between gap-2">
                      <span className="text-red-600">INCALL</span>
                      <span data-testid="agent-board-incall">{counts.incall ?? 0}</span>
                    </p>
                    <p className="flex justify-between gap-2">
                      <span className="text-amber-600">PAUSED</span>
                      <span data-testid="agent-board-paused">{counts.paused ?? 0}</span>
                    </p>
                  </div>
                  <ul className="mt-3 space-y-1.5" data-testid="agent-ws-status-list">
                    {agents.length === 0 ? (
                      <li className="text-[11px] text-muted-foreground">No agents logged into this campaign.</li>
                    ) : agents.map((a) => (
                      <li
                        key={a.session_id || a.user_id}
                        className="flex justify-between gap-2 text-[11px]"
                        data-testid="agent-board-row"
                      >
                        <span className={`truncate font-medium ${a.self ? "text-foreground" : "text-slate-700"}`} title={a.name}>
                          {a.name}{a.self ? " (you)" : ""}
                        </span>
                        <span className={`shrink-0 font-semibold ${tone(a.status)}`}>
                          {a.status_label || a.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              );
            })()}
            {busy && (
              <p className="mt-3 text-[11px] text-muted-foreground" data-testid="agent-ws-busy">
                Working… ({busyAction || "request"})
              </p>
            )}
          </div>
          </div>
          </>
          )}
        </aside>
      </div>

      <Dialog open={manualCallOpen} onOpenChange={setManualCallOpen}>
        <DialogContent className="max-w-sm" data-testid="agent-manual-call-dialog">
          <DialogHeader>
            <DialogTitle>Manual Call</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="manual-call-number">Phone number</Label>
            <Input
              id="manual-call-number"
              inputMode="tel"
              autoComplete="tel"
              placeholder="e.g. +15551234567"
              value={manualCallNumber}
              onChange={(e) => setManualCallNumber(e.target.value)}
              data-testid="agent-manual-call-number"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitManualCall();
                }
              }}
            />
            <p className="text-[11px] text-muted-foreground">
              Enter the number to dial. Digits, +, and spaces are allowed.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setManualCallOpen(false)}
              data-testid="agent-manual-call-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={busy || !(manualCallNumber || "").replace(/\D/g, "").length}
              onClick={submitManualCall}
              data-testid="agent-manual-call-submit"
            >
              {busyAction === "manual_dial" ? "Dialing…" : "Dial"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={messageOpen} onOpenChange={setMessageOpen}>
        <DialogContent className="max-w-md" data-testid="agent-messages-dialog">
          <DialogHeader>
            <DialogTitle>Send message</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {(messagingSms.length > 0 && messagingEmail.length > 0) && (
              <div className="flex gap-2" data-testid="agent-messages-channel-tabs">
                <Button
                  type="button"
                  size="sm"
                  variant={messageChannel === "sms" ? "default" : "outline"}
                  onClick={() => {
                    setMessageChannel("sms");
                    setMessageProviderId(messagingSms[0]?.id || "");
                    setMessageTo(form.phone || customerInfo?.phone || "");
                  }}
                  data-testid="agent-messages-channel-sms"
                >
                  SMS
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={messageChannel === "email" ? "default" : "outline"}
                  onClick={() => {
                    setMessageChannel("email");
                    setMessageProviderId(messagingEmail[0]?.id || "");
                    setMessageTo(form.email || customerInfo?.email || "");
                  }}
                  data-testid="agent-messages-channel-email"
                >
                  Email
                </Button>
              </div>
            )}
            {messageProviders.length > 1 && (
              <div className="space-y-1.5">
                <Label>Provider</Label>
                <Select
                  value={messageProviderId || undefined}
                  onValueChange={setMessageProviderId}
                >
                  <SelectTrigger data-testid="agent-messages-provider">
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {messageProviders.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name || p.slug}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {messageProviders.length === 1 && (
              <p className="text-xs text-muted-foreground" data-testid="agent-messages-provider-label">
                Via {messageProviders[0].name || messageProviders[0].slug}
              </p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="agent-msg-to">
                {messageChannel === "sms" ? "Phone number" : "Email address"}
              </Label>
              <Input
                id="agent-msg-to"
                inputMode={messageChannel === "sms" ? "tel" : "email"}
                autoComplete={messageChannel === "sms" ? "tel" : "email"}
                placeholder={messageChannel === "sms" ? "+15551234567" : "customer@example.com"}
                value={messageTo}
                onChange={(e) => setMessageTo(e.target.value)}
                data-testid="agent-messages-to"
              />
            </div>
            {messageChannel === "email" && (
              <div className="space-y-1.5">
                <Label htmlFor="agent-msg-subject">Subject</Label>
                <Input
                  id="agent-msg-subject"
                  value={messageSubject}
                  onChange={(e) => setMessageSubject(e.target.value)}
                  placeholder="Subject"
                  data-testid="agent-messages-subject"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="agent-msg-body">Message</Label>
              <Textarea
                id="agent-msg-body"
                rows={5}
                value={messageBody}
                onChange={(e) => setMessageBody(e.target.value)}
                placeholder={messageChannel === "sms" ? "SMS text…" : "Email body…"}
                data-testid="agent-messages-body"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setMessageOpen(false)}
              data-testid="agent-messages-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={
                busy
                || !messageProviderId
                || !(messageTo || "").trim()
                || !(messageBody || "").trim()
              }
              onClick={submitOutboundMessage}
              data-testid="agent-messages-submit"
            >
              {busyAction === "send_message"
                ? "Sending…"
                : (messageChannel === "sms" ? "Send SMS" : "Send Email")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ingroupOpen} onOpenChange={setIngroupOpen}>
        <DialogContent className="max-w-sm" data-testid="agent-ingroup-dialog">
          <DialogHeader>
            <DialogTitle>Inbound groups</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-auto">
            {availableIngroups.length === 0 ? (
              <p className="text-sm text-muted-foreground">No inbound groups on this campaign.</p>
            ) : availableIngroups.map((g) => (
              <label
                key={g.id}
                className="flex items-center gap-2 text-sm"
                data-testid={`agent-ingroup-row-${g.id}`}
              >
                <Checkbox
                  checked={ingroupDraft.includes(g.id)}
                  onCheckedChange={(on) => {
                    setIngroupDraft((prev) => toggleInboundGroupId(prev, g.id, on === true));
                  }}
                  data-testid={`agent-ingroup-check-${g.id}`}
                />
                <span className="truncate">{g.name}</span>
              </label>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIngroupOpen(false)}
              data-testid="agent-ingroup-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={ingroupBusy || busy}
              onClick={() => runLocked("inbound_groups", async () => {
                setIngroupBusy(true);
                try {
                  const { data } = await api.post("/agent/session/inbound-groups", {
                    inbound_group_ids: ingroupDraft,
                  });
                  if (data?.workspace) applyWorkspace(data.workspace);
                  else if (data?.inbound_groups) {
                    setWorkspace((prev) => ({ ...(prev || {}), inbound_groups: data.inbound_groups }));
                  }
                  setIngroupOpen(false);
                  toast.success("Inbound groups updated");
                } catch (e) {
                  setError(formatApiError(e.response?.data?.detail, e));
                } finally {
                  setIngroupBusy(false);
                }
              })}
              data-testid="agent-ingroup-save"
            >
              {ingroupBusy || busyAction === "inbound_groups" ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="max-w-md" data-testid="agent-transfer-dialog">
          <DialogHeader>
            <DialogTitle>Transfer Call</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5" data-testid="agent-transfer-type">
              {[
                { id: "agent", label: "Direct agent" },
                { id: "inbound_group", label: "Inbound group" },
                { id: "campaign", label: "Campaign" },
                { id: "third_party", label: "3rd Party" },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`agent-transfer-type-${t.id}`}
                  onClick={() => {
                    setTransferType(t.id);
                    setTransferTargetId("");
                    setTransferPhone("");
                  }}
                  className={
                    "h-9 rounded-md border text-[11px] font-medium px-1 "
                    + (transferType === t.id
                      ? "border-sky-600 bg-sky-50 text-sky-800"
                      : "border-border text-muted-foreground hover:bg-muted")
                  }
                >
                  {t.label}
                </button>
              ))}
            </div>
            {transferTargetsLoading ? (
              <p className="text-sm text-muted-foreground">Loading destinations…</p>
            ) : transferType === "third_party" ? (
              <div className="space-y-1.5" data-testid="agent-transfer-third-party">
                <Label htmlFor="agent-transfer-phone">Phone number</Label>
                <Input
                  id="agent-transfer-phone"
                  className="h-10 rounded-sm"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="Enter number to transfer"
                  value={transferPhone}
                  onChange={(e) => setTransferPhone(e.target.value)}
                  data-testid="agent-transfer-phone"
                />
                {transferTargets.third_party?.available ? (
                  <p className="text-[11px] text-muted-foreground">
                    Routed via campaign vendor
                    {transferTargets.third_party.vendor_name
                      ? ` · ${transferTargets.third_party.vendor_name}`
                      : ""}
                  </p>
                ) : (
                  <p className="text-[11px] text-amber-700" data-testid="agent-transfer-no-vendor">
                    This campaign has no active vendor route. Assign a vendor before 3rd Party transfer.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>
                  {transferType === "agent"
                    ? "Agent"
                    : transferType === "inbound_group"
                      ? "Inbound group"
                      : "Campaign"}
                </Label>
                <Select
                  value={transferTargetId}
                  onValueChange={setTransferTargetId}
                >
                  <SelectTrigger data-testid="agent-transfer-target" className="h-10">
                    <SelectValue placeholder="Select destination" />
                  </SelectTrigger>
                  <SelectContent>
                    {transferType === "agent" && (transferTargets.agents || []).map((a) => (
                      <SelectItem key={a.id} value={a.id} data-testid={`agent-transfer-agent-${a.id}`}>
                        {a.name}{a.sip_username ? ` · ${a.sip_username}` : ""}
                      </SelectItem>
                    ))}
                    {transferType === "inbound_group" && (transferTargets.inbound_groups || []).map((g) => (
                      <SelectItem key={g.id} value={g.id} data-testid={`agent-transfer-group-${g.id}`}>
                        {g.name}
                      </SelectItem>
                    ))}
                    {transferType === "campaign" && (transferTargets.campaigns || []).map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`agent-transfer-campaign-${c.id}`}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {transferType === "agent" && !(transferTargets.agents || []).length && (
                  <p className="text-[11px] text-muted-foreground">No other agents with a softphone.</p>
                )}
                {transferType === "inbound_group" && !(transferTargets.inbound_groups || []).length && (
                  <p className="text-[11px] text-muted-foreground">No inbound groups assigned to you.</p>
                )}
                {transferType === "campaign" && !(transferTargets.campaigns || []).length && (
                  <p className="text-[11px] text-muted-foreground">No campaigns assigned to you.</p>
                )}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">
              Stay keeps you on the call after the destination is rung. Leave hands the caller off and ends your leg.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:justify-between flex-col sm:flex-row">
            <Button
              type="button"
              variant="outline"
              onClick={() => setTransferOpen(false)}
              data-testid="agent-transfer-cancel"
            >
              Cancel
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={busy || !transferReady || (transferType === "third_party" && !transferTargets.third_party?.available)}
                onClick={() => submitTransfer(true)}
                data-testid="agent-transfer-stay"
              >
                {busyAction === "transfer" ? "Transferring…" : "Transfer & stay"}
              </Button>
              <Button
                type="button"
                disabled={busy || !transferReady || (transferType === "third_party" && !transferTargets.third_party?.available)}
                onClick={() => submitTransfer(false)}
                data-testid="agent-transfer-leave"
              >
                {busyAction === "transfer" ? "Transferring…" : "Transfer & leave"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(dueModalCb)}
        onOpenChange={(open) => {
          if (!open && dueModalCb?.id) {
            // Closing dismisses this + other already-due ones so they don't chain
            dismissDueCallback(dueModalCb.id, { dismissAllDue: true });
          }
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="agent-callback-due-modal">
          <DialogHeader>
            <DialogTitle>
              {dueModalCb?.missed ? "Missed callback" : "Callback now"}
            </DialogTitle>
          </DialogHeader>
          {dueModalCb ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">Customer</span>
                <span className="font-medium text-right">
                  {dueModalCb.customer_name || "—"}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">Phone</span>
                <span className="font-mono">{dueModalCb.phone || "—"}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">When</span>
                <span className="text-right">
                  {dueModalCb.scheduled_display || dueModalCb.scheduled_at || "—"}
                  {dueModalCb.missed ? (
                    <span className="ml-1 text-red-700 font-medium">Missed</span>
                  ) : null}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">Scheduled by</span>
                <span className="text-right">{dueModalCb.agent_name || "—"}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">Type</span>
                <span>{dueModalCb.private ? "My Call Back" : "Campaign"}</span>
              </div>
              {dueModalCb.campaign_name ? (
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Campaign</span>
                  <span className="text-right truncate max-w-[14rem]">{dueModalCb.campaign_name}</span>
                </div>
              ) : null}
              <div className="pt-1">
                <p className="text-muted-foreground text-xs mb-1">Comments</p>
                <p className="rounded-sm border border-border bg-muted/40 px-2.5 py-2 text-xs whitespace-pre-wrap min-h-[2.5rem]">
                  {dueModalCb.comments || dueModalCb.notes || "—"}
                </p>
              </div>
            </div>
          ) : null}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              className="rounded-sm"
              onClick={() => dismissDueCallback(dueModalCb?.id, { dismissAllDue: true })}
              data-testid="agent-callback-due-close"
            >
              Close
            </Button>
            <Button
              type="button"
              className="rounded-sm"
              disabled={busy || onCall || dispoRequired || !dueModalCb}
              onClick={() => dueModalCb && callbackCallNow(dueModalCb)}
              data-testid="agent-callback-due-call-now"
            >
              Call back now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={statsOpen} onOpenChange={setStatsOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="agent-stats-dialog">
          <DialogHeader>
            <DialogTitle>Today&apos;s stats</DialogTitle>
          </DialogHeader>
          {dayStats ? (
            <dl className="space-y-2.5 text-sm" data-testid="agent-day-stats">
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Date</dt>
                <dd className="text-right">
                  {dayStats.date || "—"}
                  {dayStats.timezone ? (
                    <span className="ml-1 text-xs text-muted-foreground">{dayStats.timezone}</span>
                  ) : null}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Total calls</dt>
                <dd className="font-semibold tabular-nums" data-testid="agent-day-stats-calls">
                  {dayStats.total_calls ?? 0}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Talk time</dt>
                <dd className="font-mono" data-testid="agent-day-stats-talk">
                  {formatStatsDuration(dayStats.talk_sec)}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Pause time</dt>
                <dd className="font-mono" data-testid="agent-day-stats-pause">
                  {formatStatsDuration(dayStats.pause_sec)}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Dispositions</dt>
                <dd className="tabular-nums" data-testid="agent-day-stats-dispos">
                  {dayStats.dispositions ?? 0}
                </dd>
              </div>
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">No stats available.</p>
          )}
          <DialogFooter>
            <Button
              type="button"
              className="rounded-sm"
              onClick={() => setStatsOpen(false)}
              data-testid="agent-stats-close"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
    {webphonePortal}
    </>
  );
}
